import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import { createPromoCode } from '../../utils/api/promoCode';

function AddPromoCodeModal({ show, onClose }) {
  const user = useSelector((state) => state.user?.user);
  const adminId = user?.id || '';

  const [formData, setFormData] = useState({
    code: '',
    type: 'flat',
    value: '',
    minPurchaseMinutes: '',
    expiresAt: '',
    usageLimit: '',
    createdByAdminId: adminId
  });

  const [loading, setLoading] = useState(false);

  // Autofill admin ID on user change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      createdByAdminId: adminId
    }));
  }, [adminId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitFormData = async (e) => {
    e.preventDefault();

    // Validate all required fields
    const { code, type, value, minPurchaseMinutes, usageLimit, createdByAdminId } = formData;
    if (!code || !type || !value || !minPurchaseMinutes || !usageLimit || !createdByAdminId) {
      toast.error('Please fill all required fields.');
      return;
    }

    setLoading(true);
    try {
      await createPromoCode(formData);
      toast.success('Promo code created successfully!');
      onClose();
    } catch (err) {
      console.error("Error creating promo code:", err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <motion.div
      className="modal fade show"
      tabIndex="-1"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 1050,
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" style={{
          borderRadius: '16px',
          border: 'none',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}>
          <div className="modal-header">
            <h5 className="modal-title">Add New Promo Code</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmitFormData}>
            <div className="modal-body">
              <div className="row g-3">

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Promo Code*</label>
                  <input
                    type="text"
                    name="code"
                    className="form-control"
                    value={formData.code}
                    onChange={handleChange}
                    required
                    placeholder="e.g., SAVE10"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Discount Type*</label>
                  <select
                    name="type"
                    style={{
                      backgroundPosition: 'right 15px center',
                    }}
                    className="form-select"
                    value={formData.type}
                    onChange={handleChange}
                    required
                  >
                    <option value="flat">Flat</option>
                    <option value="percentage">Percentage</option>
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Discount Value*</label>
                  <input
                    type="number"
                    name="value"
                    className="form-control"
                    value={formData.value}
                    onChange={handleChange}
                    min={0}
                    required
                    placeholder="Amount or percentage"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Minimum Purchase Minutes*</label>
                  <input
                    type="number"
                    name="minPurchaseMinutes"
                    className="form-control"
                    value={formData.minPurchaseMinutes}
                    onChange={handleChange}
                    min={0}
                    required
                    placeholder="e.g., 30"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Usage Limit*</label>
                  <input
                    type="number"
                    name="usageLimit"
                    className="form-control"
                    value={formData.usageLimit}
                    onChange={handleChange}
                    min={1}
                    required
                    placeholder="e.g., 100"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Expiration Date (optional)</label>
                  <input
                    type="datetime-local"
                    name="expiresAt"
                    className="form-control"
                    value={formData.expiresAt}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">Created By (Admin ID)*</label>
                  <input
                    type="text"
                    name="createdByAdminId"
                    className="form-control"
                   
                    value={formData.createdByAdminId}
                    onChange={handleChange}
                    required
                    placeholder="Admin UUID"
                    readOnly
                  />
                </div>

              </div>
            </div>

            <div className="modal-footer d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Create Promo Code'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  );
}

export default AddPromoCodeModal;
